<template>
  <div class="home-view">
    <div class="home-grid">
      <!-- Agenda: Upcoming Deadlines -->
      <div class="home-card">
        <div class="home-card__header">
          <span class="home-card__title">Agenda</span>
          <span class="home-card__count" v-if="upcomingDeadlines.length">{{ upcomingDeadlines.length }}</span>
        </div>
        <div class="agenda-list" v-if="upcomingDeadlines.length">
          <div
            v-for="task in upcomingDeadlines"
            :key="task.id"
            class="agenda-row"
            :class="{
              'agenda-row--overdue': isOverdue(task.deadline),
              'agenda-row--soon': isSoon(task.deadline) && !isOverdue(task.deadline),
            }"
            @click="$router.push({ name: 'tasks', params: { projectId: task.projectId } })"
          >
            <div class="agenda-row__date">
              <span class="agenda-row__day">{{ fmtDay(task.deadline) }}</span>
              <span class="agenda-row__mon">{{ fmtMon(task.deadline) }}</span>
            </div>
            <div class="agenda-row__info">
              <span class="agenda-row__title">{{ taskTitle(task) }}</span>
              <span class="agenda-row__proj">{{ projectName(task.projectId) }}</span>
            </div>
            <span v-if="isOverdue(task.deadline)" class="agenda-row__tag agenda-row__tag--overdue">Overdue</span>
            <span v-else-if="isSoon(task.deadline)" class="agenda-row__tag agenda-row__tag--soon">Soon</span>
          </div>
        </div>
        <EmptyState v-else>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="width:28px;height:28px;opacity:0.3;">
            <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
          <span>No upcoming deadlines</span>
        </EmptyState>
      </div>

      <!-- Time Today -->
      <div class="home-card">
        <div class="home-card__header">
          <span class="home-card__title">Time today</span>
        </div>
        <div v-if="todaySeconds > 0">
          <div class="time-today">
            <span class="time-today__value">{{ todayFormatted }}</span>
            <span class="time-today__label">tracked today</span>
          </div>
          <div class="time-by-project" v-if="todayByProject.length">
            <div
              v-for="p in todayByProject"
              :key="p.projectId"
              class="time-proj-row"
            >
              <span class="time-proj-row__name">{{ p.name }}</span>
              <span class="time-proj-row__val">{{ p.formatted }}</span>
            </div>
          </div>
        </div>
        <EmptyState v-else>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:28px;height:28px;opacity:0.3;">
            <circle cx="12" cy="12" r="9"/><path d="M12 7v5.5l3 3"/>
          </svg>
          <span>No time entries today</span>
        </EmptyState>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { getFirstLineFromHtml, durationAsSeconds, secondsFormat } from '@/utils'
  import EmptyState from '../common/EmptyState.vue'

  export default {
    name: 'Dashboard',
    components: { EmptyState },
    computed: {
      tasks() {
        return this.$store.getters.tasks
      },
      projects() {
        return this.$store.getters.projects
      },
      timeEntries() {
        return this.$store.getters.timeEntries
      },
      upcomingDeadlines() {
        return this.tasks
          .filter(t => t.deadline && !t.archivedAt && !t.completedAt)
          .sort((a, b) => moment(a.deadline).diff(moment(b.deadline)))
          .slice(0, 12)
      },
      todaySeconds() {
        const start = moment().startOf('day')
        const end = moment().endOf('day')
        return this.timeEntries
          .filter(e => moment(e.start).isBetween(start, end, null, '[]'))
          .reduce((acc, e) => acc + durationAsSeconds(e.start, e.end || moment().format()), 0)
      },
      todayFormatted() {
        return secondsFormat(this.todaySeconds)
      },
      todayByProject() {
        const start = moment().startOf('day')
        const end = moment().endOf('day')
        const byProject = {}
        for (const e of this.timeEntries) {
          if (!moment(e.start).isBetween(start, end, null, '[]')) continue
          if (!byProject[e.projectId]) byProject[e.projectId] = { projectId: e.projectId, seconds: 0 }
          byProject[e.projectId].seconds += durationAsSeconds(e.start, e.end || moment().format())
        }
        return Object.values(byProject)
          .filter(p => p.seconds > 0)
          .map(p => ({ ...p, name: this.projectName(p.projectId), formatted: secondsFormat(p.seconds) }))
          .sort((a, b) => b.seconds - a.seconds)
      }
    },
    methods: {
      taskTitle(task) {
        return getFirstLineFromHtml(task.description) || 'Untitled'
      },
      projectName(projectId) {
        const proj = this.projects.find(p => p.id === projectId)
        return proj ? proj.title : ''
      },
      fmtDay(date) { return moment(date).format('D') },
      fmtMon(date) { return moment(date).format('MMM') },
      isOverdue(date) { return moment(date).isBefore(moment(), 'day') },
      isSoon(date) { return moment(date).diff(moment(), 'days') <= 7 }
    }
  }
</script>

<style scoped>
.home-view {
  padding: 8px;
  height: 100%;
  overflow: auto;
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 8px;
  max-width: 900px;
}

.home-card {
  background: var(--app-bg-card);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  padding: 20px;
}

.home-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.home-card__title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0,0,0,0.4);
}

.home-card__count {
  background: var(--primary-muted);
  color: var(--primary);
  font-size: 0.7rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 100px;
}

/* Agenda */
.agenda-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agenda-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  transition: background 0.12s;
  cursor: pointer;
}

.agenda-row:hover { background: rgba(0,0,0,0.03); }

.agenda-row--overdue .agenda-row__day,
.agenda-row--overdue .agenda-row__mon { color: #b03a3a; }

.agenda-row--soon .agenda-row__day,
.agenda-row--soon .agenda-row__mon { color: #b87328; }

.agenda-row__date {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 32px;
  flex-shrink: 0;
}

.agenda-row__day {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(0,0,0,0.75);
  line-height: 1;
}

.agenda-row__mon {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0,0,0,0.4);
  margin-top: 1px;
}

.agenda-row__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.agenda-row__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0,0,0,0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agenda-row__proj {
  font-size: 0.72rem;
  color: rgba(0,0,0,0.4);
}

.agenda-row__tag {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 100px;
  flex-shrink: 0;
}

.agenda-row__tag--overdue {
  background: rgba(190,60,60,0.1);
  color: #b03a3a;
}

.agenda-row__tag--soon {
  background: rgba(180,120,40,0.12);
  color: #b87328;
}

/* Time today */
.time-today {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 4px;
}

.time-today__value {
  font-size: 2rem;
  font-weight: 700;
  color: rgba(0,0,0,0.8);
  line-height: 1;
}

.time-today__label {
  font-size: 0.8rem;
  color: rgba(0,0,0,0.4);
}

.time-by-project {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-proj-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(0,0,0,0.025);
}

.time-proj-row__name {
  font-size: 0.8rem;
  color: rgba(0,0,0,0.65);
}

.time-proj-row__val {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(0,0,0,0.7);
}

</style>
